import type { ActionStatus } from './deal';

/**
 * Canonical Referral lifecycle. Mirrors `Referral.statuses` in
 * `marketpryce-rewards/app/models/referral.rb`. The four states are
 * traversed one-way: pending → activated → qualified → paid.
 *
 * - `pending`: referee signed up, no Action click yet.
 * - `activated`: referee has clicked at least one deal (any Action exists).
 * - `qualified`: referee has verified an Action on a deal whose
 *   `referrer_cut_cents > 0` (i.e. the referrer is owed money once paid).
 * - `paid`: EarningsCreator has booked the referrer's cut.
 */
export const REFERRAL_STATUS = {
  Pending: 'pending',
  Activated: 'activated',
  Qualified: 'qualified',
  Paid: 'paid',
} as const;
export type ReferralStatus = (typeof REFERRAL_STATUS)[keyof typeof REFERRAL_STATUS];

/**
 * Canonical ReferralLink kinds. Mirrors `ReferralLink::KINDS`.
 *
 * - `generic`: one-per-user; URL is `?ref=<user.ref_id>`. Auto-created
 *   on first list call. Not archivable.
 * - `person`: tracked link tied to a specific recipient (email/phone
 *   stored hashed + masked). URL is `?r=<token>`.
 * - `campaign`: tracked link tied to a channel/event/group (Instagram,
 *   campus, podcast, etc.). URL is `?r=<token>`.
 */
export const REFERRAL_LINK_KIND = {
  Generic: 'generic',
  Person: 'person',
  Campaign: 'campaign',
} as const;
export type ReferralLinkKind = (typeof REFERRAL_LINK_KIND)[keyof typeof REFERRAL_LINK_KIND];

/**
 * Conventional default-channel values for ReferralLink. Backend stores
 * this as a free-text string — these are the values the share menu
 * exposes today. Use the typed enum on send; accept any string on
 * receive (in case admin tools introduce new channels).
 */
/**
 * Server-recognized `source` tags for ReferralLink. Mirrors
 * `ReferralLink::SOURCES`. Set at creation and immutable — money-grade
 * attribution for source-conditioned bonuses (e.g. QR week), so the
 * client never edits it after mint.
 *
 * - `qr`: minted by the QrSheet so QR scans attribute as link_source=qr.
 */
export const REFERRAL_LINK_SOURCE = {
  Qr: 'qr',
} as const;
export type ReferralLinkSource = (typeof REFERRAL_LINK_SOURCE)[keyof typeof REFERRAL_LINK_SOURCE];

export const REFERRAL_LINK_CHANNEL = {
  Instagram: 'instagram',
  TikTok: 'tiktok',
  Campus: 'campus',
  Text: 'text',
  Email: 'email',
  Copy: 'copy',
  Other: 'other',
} as const;
export type ReferralLinkChannel =
  (typeof REFERRAL_LINK_CHANNEL)[keyof typeof REFERRAL_LINK_CHANNEL];

/**
 * Each referee carries 0..N Actions — one per deal they've engaged
 * with. The Refer tab pivots referrals by `deal_id` to render
 * per-deal funnels (clicks → signups → paid).
 */
export interface ReferralAction {
  id: number;
  deal_id: number;
  deal_slug: string;
  status: ActionStatus;
  active_step_key: string | null;
  clicked_at: string | null;
  verified_at: string | null;
}

export interface ReferredUser {
  id: number;
  first_name: string;
  last_name: string;
  public_name: string;
  initials: string;
  school_id: number | null;
  sport: string | null;
}

export interface Referral {
  id: number;
  status: ReferralStatus;
  signed_up_at: string;
  activated_at: string | null;
  qualified_at: string | null;
  paid_at: string | null;
  /** Originating link's kind (person/campaign/generic), or null if no click was attributed. */
  source_kind: string | null;
  /** Human-friendly originating-link label ("Sarah L.", "Spring Tour", "Instagram"). */
  source_label: string | null;
  referred_user: ReferredUser;
  actions: ReferralAction[];
}

export interface ReferralLink {
  id: number;
  kind: ReferralLinkKind;
  /**
   * The deal this link is anchored to. Null only for `generic` (the
   * app-invite link) — its public_url lands on the root, not a deal page.
   */
  deal_id: number | null;
  label: string;
  default_channel: string | null;
  /**
   * Attribution tag frozen at creation ('qr' today), or null for regular
   * links. Optional: older cached payloads predate the field. A sourced
   * link is NOT interchangeable with the deal's plain share link — the
   * lookup helpers in referralLinks.store keep the two pools separate.
   */
  source?: string | null;
  token: string;
  public_url: string;
  click_count: number;
  signed_up_count: number;
  paid_count: number;
  first_clicked_at: string | null;
  last_clicked_at: string | null;
  signed_up_user_id: number | null;
  signed_up_at: string | null;
  archived_at: string | null;
  created_at: string;
  /**
   * Decrypted recipient phone/email — owner-only. Backend serves it on
   * referral_links#index for the requesting user's own links. Used by
   * the bulk-text Sending screen to fire iOS Messages with the right
   * phone pre-filled. NOT exposed for other users' links.
   */
  recipient_contact: string | null;
  recipient_contact_mask: string | null;
  /**
   * Most-recent Reminder timestamp for this link. Powers the
   * "Reminded N days ago" caption on the Refer tab Contacts row.
   * Soft truth — see backend Reminder model docs for why this is
   * named "messages_opened" rather than "sent."
   */
  last_messages_opened_at: string | null;
  /**
   * Earliest Reminder timestamp for this link. Powers the "Sent
   * Mar 14" half of the Outreach row's two-line metadata. null
   * when no reminders have been logged yet.
   */
  first_messages_opened_at: string | null;
}

/**
 * Per-deal referral money actually earned — server-summed from booked
 * Earnings (backend PR #149), so mixed-rate history (bonus weeks, rate
 * changes) stays correct forever. Replaces the old client-side
 * `paid count × referrer_cut_cents` estimate.
 */
export interface PerDealEarnedSummary {
  deal_id: number;
  earned_cents: number;
}

export interface ReferralsListResponse {
  referrals: Referral[];
  /** Optional: older servers don't send it; treat as no per-deal sums. */
  summary?: {
    per_deal: PerDealEarnedSummary[];
  };
}

export interface ReferralLinksListResponse {
  referral_links: ReferralLink[];
}

/** Array-shape response from POST /api/v1/referral_links (atomic batch create). */
export interface ReferralLinksBatchResponse {
  referral_links: ReferralLink[];
}

/**
 * One row of the Refer tab "Who's clicked" surface. v3 (2026-05-11)
 * collapsed Outreach + Funnel into one unified feed: every signed-up
 * referee shows up here regardless of how they arrived. Anonymous-click
 * rows are no longer returned (server-side change).
 */
export interface EveryoneFeedItem {
  /** Always 'signup' in v3 — the older 'anonymous' kind is gone. */
  kind: 'signup';
  stage: 'in_progress' | 'paid';
  /** Public name (e.g. "Jane S."). */
  name: string;
  /** First name only — used in the nudge-list row's two-line display. */
  first_name: string;
  /** Set on every v3 row so the detail sheet can open. */
  referral_id: number;
  /** Referee's User id (always present in v3). */
  referred_user_id: number;
  /** Originating link id, or null if no click attribution was possible. */
  referral_link_id: number | null;
  /**
   * Server-resolved share URL for the nudge text's {{your_link}} tag —
   * the referee's own attribution link, else the ambassador's canonical
   * link for the deal. Null only if the ambassador has zero active links.
   */
  share_url: string | null;
  signed_up_at: string | null;
  paid_at: string | null;
  /** ReferralLink.kind that originated this row, or null. */
  source_kind: string | null;
  /**
   * Funnel-shaped label: "Direct" for person-link sources, group label
   * for campaigns, channel name (Instagram/TikTok/etc) for generic.
   */
  source_label: string | null;
  /**
   * Referee's MarketPryce-stored phone in E.164 format. Used by the
   * nudge flow to open iMessage prefilled. Referee consented to this
   * exposure via the signup ToS. Null only in the edge case where
   * signup somehow completed without a phone — non-nudgeable.
   */
  phone_e164: string | null;
  /**
   * Referrer removed this row from their list. Rows are only returned
   * with `hidden: true` when the feed was fetched with `stage: 'removed'`,
   * so the default feed never carries them.
   *
   * Invariant, enforced server-side: hidden implies NOT paid. Paying a
   * referral clears the flag, so a removed person who eventually converts
   * comes back as PAID rather than staying invisible with money attached.
   */
  hidden: boolean;
  hidden_at: string | null;
  /**
   * Whether the server would accept hiding this row. Hiding is global (one
   * referral per friend) and refused once ANY deal paid, so a friend who is
   * in progress on this deal but paid on another cannot be removed. Absent
   * against an older server (treat as true).
   */
  can_hide?: boolean;
  /**
   * Deal Steps v2 (2026-09-03): the furthest step the provider confirmed
   * for this friend ({name, at}). Superseded by `progress` below (2026-09-16)
   * and no longer rendered; the server keeps sending it for older bundles.
   */
  confirmed_step?: { name: string | null; at: string } | null;
  /**
   * Where the friend is, in the ambassador's words (backend #165, 2026-09).
   * The row and the detail sheet render it verbatim; the client never
   * derives tone, tab or copy from the raw action. Absent against an older
   * server, in which case the row falls back to signup date + stage.
   */
  progress?: FriendProgress;
}

/** The friend's tone: color of the row's status line. */
export type FriendProgressTone = 'paid' | 'active' | 'overdue' | 'failed' | 'not_started';
/** The tab the friend sits in. `started` includes failed rows. */
export type FriendProgressStage = 'paid' | 'started' | 'not_started';
export type FriendStepState = 'confirmed' | 'waiting' | 'locked';

export interface FriendStep {
  key: string;
  /** The step as the deal names it for the person doing it. */
  name: string;
  /** The step in the ambassador's words, past tense ("Opened SoFi"). */
  label: string;
  state: FriendStepState;
  /** When the provider confirmed it; null until then. */
  at: string | null;
}

export interface FriendProgress {
  tone: FriendProgressTone;
  stage: FriendProgressStage;
  /** 1-based position of the furthest confirmed tappable step; 0 before any. */
  step_index: number;
  step_count: number;
  /** Line 3 prefix: what they last did ("Opened SoFi"; "Signed up" before any). */
  step_label: string;
  /** Line 2: what happens next ("Needs to add $10", "Paid you $20", "Didn't qualify"). */
  next_label: string;
  /** The payout when paid, else the latest confirmation, else signup. */
  last_update_at: string | null;
  /**
   * When they did `step_label`: that step's own confirmation, else the
   * payout / failure / signup date. Dates line 3 and the sheet's identity
   * line; `last_update_at` (the payout when paid) only orders the list.
   * Optional so a build ahead of its backend falls back to `last_update_at`.
   */
  step_at?: string | null;
  /** Set only when overdue: `last_update_at` + the provider's confirmation window. */
  stuck_since: string | null;
  steps: FriendStep[];
}

export interface EveryoneFeedResponse {
  items: EveryoneFeedItem[];
  /**
   * v3: two-stage funnel. `all` = both; `in_progress` = signed up but
   * not yet paid; `paid` = deal completed. 2026-09 added the deal
   * screen's tabs: `started` + `not_started` = `in_progress`. Optional
   * against an older server.
   *
   * These always describe the VISIBLE side of the feed, even when the
   * removed side is what's being listed — otherwise opening "Removed"
   * would inflate All / In progress while the user is reading them.
   * `removed` is a bare count of the hidden side.
   */
  chip_counts: {
    all: number;
    in_progress: number;
    paid: number;
    removed: number;
    started?: number;
    not_started?: number;
  };
}

/** Response from POST /api/v1/referrals/:id/hide and .../unhide. */
export interface ReferralHideResponse {
  referral: {
    id: number;
    hidden: boolean;
    hidden_at: string | null;
    status: ReferralStatus;
  };
  chip_counts: EveryoneFeedResponse['chip_counts'];
}

/** Saved message template owned by the current user. */
export interface UserSnippet {
  id: number;
  body: string;
  created_at: string;
  updated_at: string;
}

/**
 * Reminder log entry — one row per "iOS Messages composer opened addressed to
 * this person." Exactly one subject is set, and the two mean different things:
 *
 *   referral_id      — a nudge to someone who already signed up (Nudge screen)
 *   referral_link_id — person-link outreach to a contact
 *
 * A nudge deliberately leaves referral_link_id null: the two are separate so a
 * nudge can't surface in a link's "Last reminded" caption. See the backend
 * SCHEMA.md reminders section.
 */
export interface Reminder {
  id: number;
  referral_id: number | null;
  referral_link_id: number | null;
  messages_opened_at: string;
}

// MergeTag type and registry live in `lib/mergeTags.ts` so the type and the
// values that produce it stay in one place.
